import type { NativeEncodeDebug } from './NativeEncodeDebug';

/** What the host debug overlay shows for a WebCodecs sender, from one telemetry report. */
export type WebCodecsEncodeDebug = NativeEncodeDebug & {
  framesPerSecond: number;
  queueP50Us: number;
  queueP95Us: number;
};

const positive = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

/**
 * `hostQueueUs` is one sample per frame the sender queued since the previous health tick
 * (one second). The length is the frame rate. Width and encode time are present when the
 * sender included them.
 */
export function readWebCodecsEncodeDebug(
  body: Record<string, unknown>,
  nowMs: number,
  previousAtMs: number | undefined,
): { debug: WebCodecsEncodeDebug; atMs: number } | undefined {
  const samples = body['hostQueueUs'];
  if (!Array.isArray(samples) || samples.length < 1 || samples.length > 480) return undefined;
  const waits = samples.filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0);
  if (waits.length !== samples.length) return undefined;
  const elapsedMs = previousAtMs === undefined ? 1000 : Math.min(5000, Math.max(200, nowMs - previousAtMs));
  const sorted = [...waits].sort((a, b) => a - b);
  const width = positive(body['width']);
  const height = positive(body['height']);
  const encodeP50Ms = positive(body['encodeP50Ms']);
  const encodeP95Ms = positive(body['encodeP95Ms']);
  return {
    atMs: nowMs,
    debug: {
      framesPerSecond: waits.length * 1000 / elapsedMs,
      queueP50Us: sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(0.5 * sorted.length) - 1))],
      queueP95Us: sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(0.95 * sorted.length) - 1))],
      ...(width ? { frameWidth: Math.round(width) } : {}),
      ...(height ? { frameHeight: Math.round(height) } : {}),
      ...(encodeP50Ms ? { encodeP50Ms } : {}),
      ...(encodeP95Ms ? { encodeP95Ms } : {}),
    },
  };
}
